import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  X,
  AlertCircle,
  AlertTriangle,
  Mail,
  User,
  Paperclip,
  Check,
  PencilLine,
  Send,
  ExternalLink,
  Loader,
  Languages,
} from "lucide-react";
import { InvoiceListItem } from "../types";
import { getTemplateByStatus } from "../utils/emailTemplates";
import { generateAndDownloadPdf } from "../utils/pdfGenerator";
import { authenticatedFetch } from "../config/api";

interface EmailConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceListItem | null;
  initialSubject: string;
  initialText: string;
  onSend: (to: string, subject: string, text: string) => void;
  t: any;
  locale: string;
  tagColor?: "red" | "yellow";
  type?: "manual" | "auto";
  isEmailConfigured?: boolean;
}

const EmailConfirmationModal: React.FC<EmailConfirmationModalProps> = ({
  isOpen,
  onClose,
  invoice,
  initialSubject,
  initialText,
  onSend,
  t,
  locale,
  tagColor = "red",
  type = "manual",
  isEmailConfigured = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const [text, setText] = useState(initialText);
  const [recipientEmail, setRecipientEmail] = useState(
    invoice?.clientEmail || ""
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);
  const [templateLang, setTemplateLang] = useState<string>(locale);

  useEffect(() => {
    if (isOpen) {
      setSubject(initialSubject);
      setText(initialText);
      setRecipientEmail(invoice?.clientEmail || "");
      setIsEditing(false);
      setShowDownloadSuccess(false);
      setTemplateLang(locale);
    }
  }, [isOpen, initialSubject, initialText, locale, invoice]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const getInvoiceVariables = (inv: InvoiceListItem | null) => {
    if (!inv)
      return {
        invoiceNo: "{invoiceNo}",
        issueDate: "{issueDate}",
        amount: "{amount}",
        paymentReference: "{paymentReference}",
        paymentDetails: "{paymentDetails}",
        companyName: "{companyName}",
      };

    return {
      invoiceNo: inv.invoiceNo || "{invoiceNo}",
      issueDate: formatDate(inv.issueDate) || "{issueDate}",
      amount:
        inv.amount !== undefined
          ? `${inv.amount.toLocaleString("cs-CZ", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${inv.currency === "CZK" ? "Kč" : inv.currency}`
          : "{amount}",
      paymentReference: inv.variableSymbol || "{paymentReference}",
      paymentDetails: inv.paymentDetails || "{paymentDetails}",
      companyName: inv.sellerName || "{companyName}",
    };
  };

  const fillTemplateWithData = (
    template: string,
    inv: InvoiceListItem | null
  ) => {
    const vars = getInvoiceVariables(inv);
    return (template || "")
      .replace(/{invoiceNo}/g, vars.invoiceNo)
      .replace(/{issueDate}/g, vars.issueDate)
      .replace(/{amount}/g, vars.amount)
      .replace(/{paymentReference}/g, vars.paymentReference)
      .replace(/{paymentDetails}/g, vars.paymentDetails)
      .replace(/{companyName}/g, vars.companyName);
  };

  const handleLanguageChange = (newLang: string) => {
    setTemplateLang(newLang);
    const template = getTemplateByStatus(invoice?.status || "pending", newLang);
    setSubject(fillTemplateWithData(template.subject, invoice));
    setText(fillTemplateWithData(template.body, invoice));
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!invoice?.internalId || isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await authenticatedFetch(
        `/api/invoices/${invoice.internalId}`
      );
      if (response.ok) {
        const fullInvoiceData = await response.json();
        const activeVersion = fullInvoiceData.versions?.find(
          (v: any) => v.metadata.isActive
        );
        if (activeVersion) {
          const { draft, designSettings, language } = activeVersion;

          const invoiceTranslations = (
            await import("../translations/invoiceTranslations")
          ).invoiceTranslations;
          const lang = (language || "en") as keyof typeof invoiceTranslations;
          const translations =
            invoiceTranslations[lang] || invoiceTranslations.en;

          await generateAndDownloadPdf({
            draft,
            designSettings,
            translations,
          });
          setShowDownloadSuccess(true);
        }
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getMissingFields = (inv: InvoiceListItem | null | undefined) => {
    if (!inv)
      return [
        "invoiceNo",
        "issueDate",
        "amount",
        "paymentDetails",
        "companyName",
      ];
    const missing = [];
    if (!inv.invoiceNo) missing.push("invoiceNo");
    if (!inv.issueDate) missing.push("issueDate");
    if (inv.amount === undefined) missing.push("amount");
    if (!inv.paymentDetails) missing.push("paymentDetails");
    if (!inv.sellerName) missing.push("companyName");
    return missing;
  };

  const renderTextWithTags = (content: string) => {
    const parts = content.split(/({[a-zA-Z]+})/g);
    return parts.map((part, i) => {
      if (part.startsWith("{") && part.endsWith("}")) {
        const colorClasses =
          tagColor === "yellow"
            ? "bg-amber-100 text-amber-600 border-amber-200"
            : "bg-rose-100 text-rose-600 border-rose-200";
        return (
          <span
            key={i}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-black border animate-pulse mx-0.5 ${colorClasses}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const missingFields = getMissingFields(invoice);

  const handleSend = () => {
    if (type === "manual" && !recipientEmail.trim()) {
      alert(t.clientNotifications.noEmailError);
      return;
    }
    onSend(recipientEmail, subject, text);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200] p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-slate-900 rounded-[1.5rem] md:rounded-[2rem] shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden border border-slate-200/50 dark:border-slate-800 flex flex-col"
          >
            <div className="p-3 md:p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
              <h2 className="text-xs sm:text-base font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight truncate mr-2">
                {t.clientNotifications.confirmModalTitle}
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors shrink-0"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 custom-scrollbar">
              {/* Email Not Configured Alert - NOW FIRST and EVEN BRIGHTER */}
              {!isEmailConfigured && (
                <div className="bg-amber-200 dark:bg-amber-900/60 border-2 border-amber-500 dark:border-amber-500 rounded-2xl p-4 flex items-start gap-3 shadow-md animate-in fade-in slide-in-from-top-2 duration-500">
                  <div className="bg-amber-500 p-1.5 rounded-lg shrink-0">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[12px] md:text-[13px] font-black text-amber-950 dark:text-amber-50 uppercase tracking-tight leading-tight">
                      {t.clientNotifications.emailNotConfiguredTitle}
                    </p>
                    <p className="text-[11px] md:text-[12px] text-amber-900 dark:text-amber-200 font-black leading-normal">
                      {t.clientNotifications.emailNotConfiguredDescription
                        .split("{link}")
                        .map((part: string, index: number, arr: any[]) => (
                          <React.Fragment key={index}>
                            {part}
                            {index < arr.length - 1 && (
                              <Link
                                to={`/${locale}/client-notifications`}
                                className="text-indigo-700 dark:text-indigo-400 font-black underline decoration-4 underline-offset-4 hover:text-indigo-800 transition-colors"
                                onClick={onClose}
                              >
                                {t.clientNotifications.notificationsLink}
                                <ExternalLink className="w-3.5 h-3.5 inline-block ml-1" />
                              </Link>
                            )}
                          </React.Fragment>
                        ))}
                    </p>
                  </div>
                </div>
              )}

              {/* Missing Data Alert - NOW SECOND */}
              {missingFields.length > 0 && (
                <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/30 rounded-xl p-3 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                  <div className="space-y-1.5">
                    <p className="text-[10px] md:text-[11px] font-black text-rose-900 dark:text-rose-100 uppercase tracking-tight leading-tight">
                      {t.clientNotifications.missingFieldsTitle}
                    </p>
                    <p className="text-[9px] md:text-[10px] font-bold text-rose-600 dark:text-rose-400">
                      {missingFields
                        .map((f) => {
                          const labels: any = {
                            invoiceNo: t.invoice.invoiceNumber,
                            issueDate: t.invoice.issueDate,
                            amount: t.common.amount,
                            paymentDetails: t.invoice.paymentDetails,
                            companyName: t.common.companyName,
                          };
                          return labels[f] || f;
                        })
                        .join(", ")}
                    </p>
                    <p className="text-[9px] md:text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-normal">
                      {t.clientNotifications.missingFieldsNote
                        .split("{invoiceLink}")
                        .map((part: string, index: number, arr: any[]) => (
                          <React.Fragment key={index}>
                            {part}
                            {index < arr.length - 1 && (
                              <Link
                                to={`/${locale}/app`}
                                className="text-blue-600 dark:text-blue-400 font-bold hover:underline inline-flex items-center gap-0.5"
                                onClick={onClose}
                              >
                                {t.clientNotifications.invoiceLink}
                                <ExternalLink className="w-2.5 h-2.5" />
                              </Link>
                            )}
                          </React.Fragment>
                        ))}
                    </p>
                  </div>
                </div>
              )}

              {/* Email Edit/Preview Card */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-[1rem] md:rounded-[1.25rem] p-3 md:p-4 border border-slate-100 dark:border-slate-800 shadow-inner text-left relative group">
                {type === "manual" && (
                  <div className="flex items-center gap-2.5 mb-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400 shadow-sm border border-slate-100 dark:border-slate-700 shrink-0">
                      <User className="w-3.5 h-3.5 md:w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[7px] md:text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-0.5">
                        {t.clientNotifications.recipientEmailLabel}
                      </p>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="client@example.com"
                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-0.5 text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500/50 outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2.5 mb-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400 shadow-sm border border-slate-100 dark:border-slate-700 shrink-0">
                    <Mail className="w-3.5 h-3.5 md:w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[7px] md:text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-0.5">
                      {t.clientNotifications.subjectLabel}
                    </p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-0.5 text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500/50 outline-none"
                      />
                    ) : (
                      <p className="text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 tracking-tight truncate">
                        {renderTextWithTags(subject)}
                      </p>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={8}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 text-[11px] md:text-xs leading-relaxed text-slate-600 dark:text-slate-400 font-medium focus:ring-2 focus:ring-blue-500/50 outline-none resize-none custom-scrollbar"
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-[11px] md:text-xs leading-relaxed text-slate-600 dark:text-slate-400 font-medium overflow-y-auto max-h-[40vh] custom-scrollbar">
                    {renderTextWithTags(text)}
                  </div>
                )}
              </div>

              {/* Additional Info / Attachment / Language Selection */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className={`flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[11px] md:text-xs font-black border-2 border-blue-100 dark:border-blue-800 shadow-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isDownloading ? (
                      <Loader className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                    ) : (
                      <Paperclip className="w-4 h-4 md:w-5 md:h-5" />
                    )}
                    <span>#{invoice?.invoiceNo || "{invoiceNo}"}</span>
                  </button>

                  {showDownloadSuccess && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2 text-[10px] md:text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-xl border border-emerald-100 dark:border-emerald-800"
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>{t.clientNotifications.invoiceDownloaded}</span>
                    </motion.div>
                  )}
                </div>

                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/50 p-1 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-1.5 px-2 text-slate-400">
                    <Languages className="w-4 h-4" />
                  </div>
                  <select
                    value={templateLang}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="bg-transparent border-0 outline-none text-[10px] md:text-xs font-black text-slate-600 dark:text-slate-300 pr-8 cursor-pointer appearance-none py-1"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 0.5rem center",
                      backgroundSize: "1rem",
                    }}
                  >
                    <option value="cs">🇨🇿 Čeština</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="ru">Русский</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="p-2 md:p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex gap-2 md:gap-2.5">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex-1 py-1.5 md:py-2 px-2 md:px-4 rounded-xl font-black transition-all border flex items-center justify-center gap-1 shadow-sm text-[10px] md:text-xs ${
                  isEditing
                    ? "bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-700 shadow-emerald-100"
                    : "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                }`}
              >
                {isEditing ? (
                  <>
                    <Check className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span>{t.clientNotifications.save}</span>
                  </>
                ) : (
                  <>
                    <PencilLine className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span>{t.clientNotifications.editEmail}</span>
                  </>
                )}
              </button>
              <button
                onClick={handleSend}
                disabled={isEditing || !isEmailConfigured}
                className={`flex-[1.5] py-1.5 md:py-2 px-2 md:px-4 text-white rounded-xl font-black transition-all shadow-lg flex flex-col items-center justify-center gap-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isEditing || !isEmailConfigured
                    ? "bg-slate-400 shadow-none"
                    : "bg-purple-600 hover:bg-purple-700 shadow-purple-200 dark:shadow-none"
                }`}
              >
                <div className="flex items-center gap-1 md:gap-1.5">
                  {type === "manual" ? (
                    <>
                      <Send className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[10px] md:text-xs uppercase tracking-wide">
                        {t.clientNotifications.sendTo.charAt(0).toUpperCase() +
                          t.clientNotifications.sendTo.slice(1)}
                      </span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3 h-3 md:w-3.5 md:h-3.5" />
                      <span className="text-[10px] md:text-xs uppercase tracking-wide">
                        {t.common.save}
                      </span>
                    </>
                  )}
                </div>
                {type === "manual" && (
                  <span className="text-[8px] md:text-[9px] opacity-90 font-bold tracking-normal leading-none truncate max-w-full px-1">
                    {recipientEmail}
                  </span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EmailConfirmationModal;
